package fr.manyverse.app.presentation.components.notification

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.draggable
import androidx.compose.foundation.gestures.rememberDraggableState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Casino
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.DiceOutcome
import fr.manyverse.app.domain.model.diceOutcome
import fr.manyverse.app.presentation.theme.success
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.abs

@Composable
internal fun DiceRollNotification(
    notification: NotificationContent.DiceRoll,
    totalEnabled: Boolean = true,
    onDismiss: () -> Unit
) {
    var progress by remember(notification.id) { mutableFloatStateOf(1f) }
    val offsetX = remember(notification.id) { Animatable(0f) }
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(notification.id) {
        val steps = 100
        val stepDelay = NOTIFICATION_DURATION_MS / steps
        for (i in steps downTo 0) {
            progress = i.toFloat() / steps
            delay(stepDelay)
        }
    }

    val draggableState = rememberDraggableState { delta ->
        coroutineScope.launch { offsetX.snapTo(offsetX.value + delta) }
    }

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = MaterialTheme.colorScheme.surfaceContainerHigh,
        shadowElevation = 8.dp,
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer { translationX = offsetX.value }
            .draggable(
                orientation = Orientation.Horizontal,
                state = draggableState,
                onDragStopped = {
                    if (abs(offsetX.value) > SWIPE_DISMISS_THRESHOLD) {
                        val target = if (offsetX.value > 0) SWIPE_OUT_TARGET else -SWIPE_OUT_TARGET
                        offsetX.animateTo(target)
                        onDismiss()
                    } else {
                        offsetX.animateTo(0f)
                    }
                }
            )
    ) {
        Column {
            Row(
                modifier = Modifier.padding(horizontal = 10.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.primaryContainer,
                    modifier = Modifier.size(34.dp)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            imageVector = Icons.Default.Casino,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onPrimaryContainer,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }

                Spacer(modifier = Modifier.width(10.dp))

                val diceLabel = if (notification.count > 1) {
                    "${notification.count}×${notification.dieType}"
                } else {
                    notification.dieType
                }

                // Lanceur et dés sur la première ligne, résultat sur la seconde.
                Column(
                    verticalArrangement = Arrangement.spacedBy(2.dp),
                    modifier = Modifier.weight(1f)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        // Le nom cède la place au type de dé : tronqué plutôt que d'écraser la suite.
                        Text(
                            text = notification.characterName,
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f, fill = false)
                        )

                        Text(
                            text = " · $diceLabel",
                            fontSize = 13.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            softWrap = false
                        )
                    }

                    if (totalEnabled) {
                        val sum = notification.results.sum()
                        val total = sum + notification.modifier
                        // Les seuils critiques portent sur chaque dé : on résout les couleurs
                        // hors du builder (non composable) puis on colore le détail dé par dé.
                        val successColor = MaterialTheme.colorScheme.success
                        val errorColor = MaterialTheme.colorScheme.error
                        val normalColor = MaterialTheme.colorScheme.onSurface
                        val neutralColor = MaterialTheme.colorScheme.onSurfaceVariant
                        val colorFor: (Int) -> Color = { value ->
                            when (diceOutcome(value, notification.criticalMin, notification.criticalMax, notification.highValuesWin)) {
                                DiceOutcome.CRITICAL_SUCCESS -> successColor
                                DiceOutcome.CRITICAL_FAILURE -> errorColor
                                DiceOutcome.NORMAL -> normalColor
                            }
                        }
                        // Le total n'est coloré que s'il correspond à la valeur naturelle d'un dé unique.
                        val totalIsSingleNaturalDie = notification.results.size == 1 && notification.modifier == 0
                        val totalColor = if (totalIsSingleNaturalDie) colorFor(total) else normalColor
                        val breakdown: AnnotatedString? = if (notification.results.size > 1 || notification.modifier != 0) {
                            buildAnnotatedString {
                                withStyle(SpanStyle(color = neutralColor)) { append("(") }
                                if (notification.results.size > 1) {
                                    notification.results.forEachIndexed { index, result ->
                                        withStyle(SpanStyle(color = colorFor(result))) { append(result.toString()) }
                                        if (index < notification.results.size - 1) {
                                            withStyle(SpanStyle(color = neutralColor)) { append("+") }
                                        }
                                    }
                                } else {
                                    withStyle(SpanStyle(color = colorFor(sum))) { append(sum.toString()) }
                                }
                                if (notification.modifier != 0) {
                                    val sign = if (notification.modifier >= 0) "+" else "−"
                                    withStyle(SpanStyle(color = neutralColor)) { append("$sign${abs(notification.modifier)}") }
                                }
                                withStyle(SpanStyle(color = neutralColor)) { append(")") }
                            }
                        } else null
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(4.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Surface(
                                shape = RoundedCornerShape(4.dp),
                                color = totalColor.copy(alpha = 0.15f)
                            ) {
                                Text(
                                    text = total.toString(),
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp),
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = totalColor
                                )
                            }
                            if (breakdown != null) {
                                Text(
                                    text = breakdown,
                                    fontSize = 11.sp
                                )
                            }
                        }
                    } else {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            notification.results.forEach { result ->
                                val value = result + notification.modifier
                                val chipColor = criticalNotificationColor(
                                    diceOutcome(value, notification.criticalMin, notification.criticalMax, notification.highValuesWin)
                                )
                                Surface(
                                    shape = RoundedCornerShape(4.dp),
                                    color = chipColor.copy(alpha = 0.15f)
                                ) {
                                    Text(
                                        text = value.toString(),
                                        modifier = Modifier.padding(horizontal = 5.dp, vertical = 1.dp),
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = chipColor
                                    )
                                }
                            }
                        }
                    }
                }
            }

            LinearProgressIndicator(
                progress = { progress },
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.primary,
                trackColor = Color.Transparent
            )
        }
    }
}

@Composable
private fun criticalNotificationColor(outcome: DiceOutcome): Color = when (outcome) {
    DiceOutcome.CRITICAL_SUCCESS -> MaterialTheme.colorScheme.success
    DiceOutcome.CRITICAL_FAILURE -> MaterialTheme.colorScheme.error
    DiceOutcome.NORMAL -> MaterialTheme.colorScheme.onSurface
}